import * as React from "react"
import { Fragment, useEffect } from "react"
import {
  landingPageViewed,
  preQualLandingPageIntentClick,
} from "@/analytics/events/preQual"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import FullScreenBackgroundGradient from "@/components/backgrounds/FullScreenBackgroundGradient"
import { Body, H5, H6, Small, Tiny } from "@/components/standard/BFText"
import { Pressable, ScrollView, View } from "react-native"
import { router } from "expo-router"
import { PrimaryDarkButton } from "@/components/buttons/CommonButtons"
import { PreQualLandingHeader } from "@/screens/prequal/components/PreQualLandingHeader"

import Svg, { Ellipse, Path } from "react-native-svg"
import { DownloadAppBar } from "@/screens/prequal/components/DownloadAppBar"
import TealCheck from "@/assets/icons/teal-check.svg"
import TimeSmall from "@/assets/icons/time-small.svg"
import CheckSmall from "@/assets/icons/check-small.svg"
import BindingSmall from "@/assets/icons/binding-small.svg"

function SvgComponent() {
  return (
    <Svg width={13} height={12} viewBox="0 0 13 12" fill="none">
      <Ellipse cx={6.32202} cy={6} rx={5.82202} ry={6} fill="#75FAD4" />
      <Path
        d="M2.922 6.093c.055-.125 1.328-.064 1.713-.084 1.682-.086 3.417-.375 5.092-.098"
        fill="#121F3A"
      />
      <Path
        d="M2.922 6.093c.055-.125 1.328-.064 1.713-.084 1.682-.086 3.417-.375 5.092-.098"
        stroke="#121F3A"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M7.467 4.497c-.03.073-.233-.218-.177-.161.526.542 1.228.892 1.853 1.308.032.022.609.37.574.419-.135.188-.596.326-.79.432-.254.138-.504.284-.761.416-.356.182-.715.38-1.088.526"
        stroke="#121F3A"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

export default SvgComponent

interface TimelineData {
  title: string
  timeLeft?: string
  completed?: boolean
}

const TIMELINE_NO_QUAL: TimelineData[] = [
  {
    title: "Check Qualification",
    timeLeft: "~2 min",
  },
  {
    title: "Become a Member",
    timeLeft: "1 min",
  },
  {
    title: "Provide Property Info",
    timeLeft: "1 min",
  },
  {
    title: "Select Loan Product & Submit *",
    timeLeft: "2 min",
  },
]

const LoanTimeline = ({
  data,
  index,
}: {
  data: TimelineData
  index: number
}) => {
  return (
    <Vertical>
      <Horizontal className={"items-center gap-1"}>
        <H6 color={"teal"}>STEP {index + 1}</H6>
        {data.completed && <TealCheck />}
      </Horizontal>
      <Horizontal className={"justify-between w-full"}>
        <H5 color={"white"}>{data.title}</H5>
        {data.completed ? (
          <Small className={"color-calm-blue"}>Completed</Small>
        ) : (
          <Small color={"teal"}>{data.timeLeft}</Small>
        )}
      </Horizontal>
    </Vertical>
  )
}

const CalloutButton = ({
  title,
  text,
  cta,
}: {
  title: string
  text: string
  cta: string
}) => (
  <Vertical className={"bg-midnight-blue rounded-lg p-5 gap-1"}>
    <H5 color={"white"}>{title}</H5>
    <Tiny color={"white"}>{text}</Tiny>
    <Horizontal className={"mt-3 items-center gap-3"}>
      <Small color={"teal"}>{cta}</Small>
      <SvgComponent />
    </Horizontal>
  </Vertical>
)

export const IntentLandingScreen = () => {
  useEffect(() => {
    landingPageViewed()
  }, [])

  return (
    <Vertical className={"size-full flex-1"}>
      <FullScreenBackgroundGradient preQual={true} />

      <ScrollView className={"flex-1 h-screen"}>
        <Vertical className={"min-h-screen"}>
          <PreQualLandingHeader height={210} text={"Let's get\nyou |funded|"} />
          <Vertical
            className={"justify-between max-w-[560px] mx-auto gap-4 flex-1 p-5"}
          >
            <Vertical className={"bg-midnight-blue p-5 gap-3 rounded-lg"}>
              {TIMELINE_NO_QUAL.map((item, index) => (
                <Fragment key={index}>
                  <LoanTimeline index={index} data={item} />
                  {index !== TIMELINE_NO_QUAL.length - 1 && (
                    <View className={`border-b border-calm-blue`} />
                  )}
                </Fragment>
              ))}
            </Vertical>
            <Tiny color={"white"} className={"text-center"}>
              *Our loan experts will review terms with you before finalization
            </Tiny>
            <View className={"gap-3 items-center"}>
              <PrimaryDarkButton
                title={"Get Started"}
                testID={"get-started"}
                onPress={() => {
                  preQualLandingPageIntentClick({
                    intent: "Loan",
                    id: "get-started",
                    title: "Get Started",
                  })
                  router.push("/pre-qual/properties-flipped")
                }}
              />
              <Horizontal className={"gap-1 items-center"}>
                <TimeSmall />
                <Small color={"white"}>7 min | </Small>
                <CheckSmall />
                <Small color={"white"}>No credit impact | </Small>
                <BindingSmall />
                <Small color={"white"}>Non-binding</Small>
              </Horizontal>
            </View>
            <View className={"py-6"}>
              <View className="flex h-px bg-calm-blue m-4 " />
            </View>
            <View className={"gap-5"}>
              <Vertical className={"px-10 items-center"}>
                <H5 color={"white"}>No property, yet?</H5>
                <Body color={"white"} className={"text-center"}>
                  No problem. Backflip is your Fix & Flip partner for each stage
                  of your deal.
                </Body>
              </Vertical>
              <Horizontal
                className={"flex-1 gap-5 items-stretch justify-stretch"}
              >
                <Pressable
                  className={"flex-1"}
                  testID={"view-loan-products"}
                  onPress={() => {
                    preQualLandingPageIntentClick({
                      intent: "LoanProducts",
                      id: "view-loan-products",
                      title: "View Backflip’s Loan Products",
                    })
                    router.push("/pre-qual/loanproducts")
                  }}
                >
                  <CalloutButton
                    title={"Explore loan options"}
                    text={"Interest from 9.0%\nUp to 96% Loan to Cost"}
                    cta={"View products"}
                  />
                </Pressable>

                <Pressable
                  className={"flex-1"}
                  testID={"find-my-next-deal"}
                  onPress={() => {
                    preQualLandingPageIntentClick({
                      intent: "Analyzer",
                      id: "find-my-next-deal",
                      title: "Help Me Find My Next Deal",
                    })
                    router.push("/pre-qual/analyzer")
                  }}
                >
                  <CalloutButton
                    title={"Find your next deal"}
                    text={"Fast & free ARVs and CMAs for Backflip Members"}
                    cta={"Learn more"}
                  />
                </Pressable>
              </Horizontal>
            </View>
          </Vertical>
          <DownloadAppBar />
        </Vertical>
      </ScrollView>
    </Vertical>
  )
}
